// State machine for keeping the native app-icon badge honest after the user
// reads their messages.
//
// The badge is *set* by the push payload (send-push writes an absolute number
// per recipient), so the icon is correct the moment a message lands. What the
// server cannot do is notice that the user has since read the thread — and iOS
// leaves the last badge on the icon forever until the app clears it.
//
// The only badge control the JS layer has without a new native plugin is
// PushNotifications.removeAllDeliveredNotifications(), which the Capacitor iOS
// plugin implements as "clear Notification Center AND set the icon badge to 0".
// That is all-or-nothing: we can zero the badge, we cannot set it to 3. So the
// rule is deliberately narrow — clear once when the unread total reaches zero,
// and otherwise leave whatever number the last push wrote. A partially-read
// inbox keeps a badge that reads high until the next push corrects it, which is
// the right way to be wrong: the icon never under-reports waiting messages.

export interface BadgeSyncState {
  /** True once we've zeroed the icon for the current run of "nothing unread". */
  cleared: boolean;
}

export const initialBadgeSyncState: BadgeSyncState = { cleared: false };

/**
 * Fold a fresh unread total into the sync state.
 *
 * `total` is null/undefined while the unread query is still loading or has
 * errored — an unknown count must never be treated as zero, or a cold start
 * would wipe a legitimate badge before the first fetch resolves.
 */
export function nextBadgeSync(
  state: BadgeSyncState,
  total: number | null | undefined,
): { state: BadgeSyncState; clear: boolean } {
  if (typeof total !== 'number' || Number.isNaN(total)) {
    return { state, clear: false };
  }
  if (total > 0) {
    // Unread again — arm the next clear. (We can't write a non-zero badge from
    // here; the push that delivered these messages already did.)
    return { state: state.cleared ? { cleared: false } : state, clear: false };
  }
  if (state.cleared) return { state, clear: false };
  return { state: { cleared: true }, clear: true };
}
